import { useContrastQuery } from '../../hooks/use-contrast-query'
import { Query } from '../../hooks/queries'
import {
  OrganizationFeatureArgs,
  OrganizationFeatureResponse,
  getOrganizationFeature
} from '../../api/queries/organization-features'
import { HTTPError } from '../../api/fetch/decorate-fetch'

export type OrganizationFeatureId =
  | 'approval-workflow'
  | 'disa-stig'
  | 'oss-inventory-mode'
  | 'app-library-status'
  | 'vulnerability-auto-verification'
  | 'vulnerability-trends-graph'

const queryIds: { [key: string]: Query } = {
  'approval-workflow': Query.APPROVAL_WORKFLOW_FEATURE,
  'disa-stig': Query.DISA_AND_STIG_FEATURE,
  'oss-inventory-mode': Query.OSS_INVENTORY_MODE_FEATURE,
  'app-library-status': Query.APP_LIBRARY_STATUS_FEATURE,
  'vulnerability-auto-verification': Query.VULN_AUTO_VERIFICATION_FEATURE,
  'vulnerability-trends-graph': Query.VULN_TRENDS_GRAPH_FEATURE
}

// cache is valid for 5 minutes
export const cacheOptions = { cacheTime: 300000, staleTime: 300000 }
export interface UseOrganizationFeatureResponse {
  enabled: boolean
  isLoading: boolean
  error: HTTPError | null
}
export function useOrganizationFeature(
  organizationUuid: string,
  featureId: OrganizationFeatureId,
  enabled: boolean = true
): UseOrganizationFeatureResponse {
  const { data, isLoading, error } = useContrastQuery<
    OrganizationFeatureArgs,
    OrganizationFeatureResponse
  >(
    [queryIds[featureId], organizationUuid],
    getOrganizationFeature,
    {
      organizationUuid,
      featureId
    },
    { enabled, ...cacheOptions }
  )

  return {
    enabled: (data && data.enabled) || false,
    isLoading,
    error
  }
}
/**
 * Alternative to {@link useOrganizationFeature} intended only for use in class components.
 */
interface OrganizationFeatureProps {
  children: (res: UseOrganizationFeatureResponse) => any
  organizationUuid: string
  featureId: OrganizationFeatureId
}
export const OrganizationFeature: React.FC<OrganizationFeatureProps> = ({
  children,
  organizationUuid,
  featureId
}) => {
  const res = useOrganizationFeature(organizationUuid, featureId)
  return children(res)
}
